// MiniMeChatContext.js
// Protected surface: review docs/protected-surfaces/mini-me-chat.md before changing Mini Me launch or seed-state behavior.
// Context for controlling the MiniMe chat drawer and passing initial messages from anywhere in the app.
// Provides: open/close chat, set initial message, and state for chat visibility.

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Create the context
const MiniMeChatContext = createContext();

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

// Custom hook for easy access
export const useMiniMeChat = () => useContext(MiniMeChatContext);

// Provider component to wrap your app
export const MiniMeChatProvider = ({ children }) => {
  // Controls whether the chat is open
  const [chatOpen, setChatOpen] = useState(false);
  // Stores the initial message to seed the chat
  const [initialMessage, setInitialMessage] = useState('');
  // Stores richer handoff context when opening Mini Me from Journey/runtime
  const [launchPayload, setLaunchPayload] = useState(null);

  const openChatWithPayload = useCallback((payload = null) => {
    const normalizedPayload = isPlainObject(payload) ? { ...payload } : {};
    const seededMessage = typeof normalizedPayload.initialMessage === 'string'
      ? normalizedPayload.initialMessage
      : '';
    setInitialMessage(seededMessage);
    setLaunchPayload(Object.keys(normalizedPayload).length > 0 ? normalizedPayload : null);
    setChatOpen(true);
  }, []);

  // Open chat, optionally with a message
  const openChat = useCallback((message = '') => {
    if (isPlainObject(message)) {
      openChatWithPayload(message);
      return;
    }
    const seededMessage = typeof message === 'string' ? message : '';
    setInitialMessage(seededMessage);
    setLaunchPayload(seededMessage ? { initialMessage: seededMessage } : null);
    setChatOpen(true);
  }, [openChatWithPayload]);

  // Open chat with no message (e.g., avatar click)
  const openChatWithoutMessage = useCallback(() => {
    setInitialMessage('');
    setLaunchPayload(null);
    setChatOpen(true);
  }, []);

  // Close chat and clear initial message
  const closeChat = useCallback(() => {
    setChatOpen(false);
    setInitialMessage('');
    setLaunchPayload(null);
  }, []);

  // Clear only the initial message (does not close chat)
  const clearInitialMessage = useCallback(() => {
    setInitialMessage('');
    setLaunchPayload(null);
  }, []);

  const clearLaunchPayload = useCallback(() => {
    setLaunchPayload(null);
  }, []);

  // Listen for service worker messages (weekly check-ins)
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'WEEKLY_CHECKIN') {
        console.log('[MiniMeChat] Weekly check-in message received:', event.data);
        // Open chat with the weekly check-in message
        openChat("Hey! What's on your mind today? 💬");
      }
    };

    // Listen for messages from service worker
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  return (
    <MiniMeChatContext.Provider value={{
      chatOpen,
      initialMessage,
      launchPayload,
      openChat,
      openChatWithPayload,
      closeChat,
      openChatWithoutMessage,
      clearInitialMessage,
      clearLaunchPayload
    }}>
      {children}
    </MiniMeChatContext.Provider>
  );
}; 
